/**
 * Gestión → Administración → Consumo por cliente
 * (`/gestion/administracion/consumo`).
 *
 * Puerto de `Administration::customer_consumption` + `views/menu/administrador/customer_consumption.php`
 * y de `Customer_consumption_model`: con el documento del cliente (DNI o RUC) y un
 * rango de fechas se resume su consumo por tipo de comprobante —boletas,
 * facturas, notas de venta y los CPE de administración— con los totales de POS,
 * de administración y generales, y el detalle de cada transacción separado en
 * **POS** y **administración**.
 */

import { Download, FileSearch, Info, ShoppingCart, User, Wallet } from 'lucide-react';
import { useEffect, useState, type ChangeEvent } from 'react';

import { useQuery } from '@tanstack/react-query';

import { DonutChart } from '@/components/charts/donut-chart';
import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';

import { AdministracionNav } from '../components/administracion-nav';
import { administracionApi, useConsumo, useConsumoDetalle } from '../administracion-canjes.api';
import { clientesApi } from '../clientes.api';
import { fechaCorta, monto } from '../gestion-formato';
import type { Cliente } from '../gestion.types';

/** Primer día del mes en curso. */
function inicioDeMes(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

export function ConsumoClientePage() {
  useDocumentTitle('Gestión · Consumo por cliente');

  const [documento, setDocumento] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [errorExportar, setErrorExportar] = useState<string | null>(null);
  const [desde, setDesde] = useState(inicioDeMes);
  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 10));
  const [consultado, setConsultado] = useState(false);

  const consulta = useConsumo(documento, desde, hasta, consultado);
  const detalle = useConsumoDetalle(documento, desde, hasta, consultado);

  // Autocompletado de clientes (busca por nombre o documento).
  const [consultaCliente, setConsultaCliente] = useState('');

  useEffect(() => {
    const temporizador = setTimeout(() => setConsultaCliente(busqueda), 250);

    return () => clearTimeout(temporizador);
  }, [busqueda]);

  const sugerencias = useQuery({
    queryKey: ['gestion', 'clientes', 'sugerencias', consultaCliente],
    queryFn: ({ signal }) => clientesApi.listado({ search: consultaCliente, limit: 8 }, signal),
    enabled: consultaCliente.trim().length >= 2,
    staleTime: 60_000,
  });

  const datos = consulta.data;

  const manejarBusqueda = (evento: ChangeEvent<HTMLInputElement>) => {
    const valor = evento.target.value;
    setBusqueda(valor);
    setAbierto(true);

    const soloNumeros = valor.replace(/[^0-9]/g, '');
    setDocumento(/^[0-9]{8,12}$/.test(soloNumeros) ? soloNumeros : '');
  };

  const elegirCliente = (cliente: Cliente) => {
    setDocumento(cliente.numero_doc ?? '');
    setBusqueda(`${cliente.nombre ?? 'Cliente'} · ${cliente.numero_doc ?? ''}`);
    setAbierto(false);
    setConsultado(false);
  };

  const exportar = async () => {
    setErrorExportar(null);
    setExportando(true);

    try {
      await administracionApi.exportarConsumo(documento, desde, hasta);
    } catch (error) {
      setErrorExportar(error instanceof Error ? error.message : 'No se pudo exportar el consumo.');
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold text-ink-900">Consumo por cliente</h1>
        <p className="mt-1 text-sm text-ink-500">
          Comprobantes del cliente en el rango: boletas, facturas, notas de venta y CPE de
          administración.
        </p>
      </header>

      <AdministracionNav active="consumo" />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative">
          <Input
            label="Cliente o documento"
            placeholder="Nombre, DNI o RUC"
            leftIcon={<FileSearch className="size-4" aria-hidden="true" />}
            value={busqueda}
            onFocus={() => setAbierto(true)}
            onBlur={() => setTimeout(() => setAbierto(false), 150)}
            onChange={manejarBusqueda}
          />
          {abierto && sugerencias.data && sugerencias.data.clientes.length > 0 ? (
            <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-card border border-ink-200 bg-white py-1 shadow-xl">
              {sugerencias.data.clientes.map((cliente) => (
                <li key={cliente.id}>
                  <button
                    type="button"
                    onMouseDown={(evento) => evento.preventDefault()}
                    onClick={() => elegirCliente(cliente)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-ink-50"
                  >
                    <span className="truncate text-ink-700">{cliente.nombre ?? 'Sin nombre'}</span>
                    <span className="shrink-0 font-mono text-xs text-ink-400">{cliente.numero_doc ?? ''}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <Input
          label="Desde"
          type="date"
          value={desde}
          onChange={(evento) => setDesde(evento.target.value)}
        />
        <Input
          label="Hasta"
          type="date"
          value={hasta}
          onChange={(evento) => setHasta(evento.target.value)}
        />
        <div className="flex items-end">
          <Button
            className="w-full"
            size="sm"
            disabled={documento.trim().length < 8 || consulta.isFetching}
            loading={consulta.isFetching}
            leftIcon={<FileSearch className="size-4" aria-hidden="true" />}
            onClick={() => setConsultado(true)}
          >
            Consultar
          </Button>
        </div>
      </div>

      {!consultado ? (
        <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-14 text-center">
          <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-ink-100 text-ink-400">
            <FileSearch className="size-6" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-ink-800">
            Consulta el consumo de un cliente
          </h2>
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
            Ingresa el DNI (8 dígitos) o RUC (11 dígitos) del cliente y pulsa Consultar para ver
            sus boletas, facturas, notas de venta y CPE de administración aceptados en el rango.
          </p>
        </div>
      ) : null}

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo leer el consumo">
          {consulta.error instanceof ApiError ? consulta.error.message : 'Inténtalo de nuevo.'}
        </Alert>
      ) : null}

      {consulta.isLoading ? <Skeleton className="h-40 w-full" /> : null}

      {consultado && datos ? (
        <>
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                    <User className="size-5" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-ink-900">
                      {datos.cliente_nombre ?? `Cliente ${datos.tipo_documento} ${datos.documento}`}
                    </p>
                    <p className="text-xs text-ink-500">
                      {datos.tipo_documento} {datos.documento}
                      {desde || hasta ? ` · ${fechaCorta(desde)} → ${fechaCorta(hasta)}` : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">
                      Total consumido
                    </p>
                    <p className="text-lg font-bold text-ink-900">{monto(datos.grand_total_monto)}</p>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    loading={exportando}
                    leftIcon={<Download className="size-4" aria-hidden="true" />}
                    onClick={() => void exportar()}
                  >
                    Exportar
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {errorExportar ? (
            <Alert variant="error" title="No se pudo exportar">
              {errorExportar}
            </Alert>
          ) : null}

          <Card>
            <CardContent className="p-4">
              <DonutChart
                labels={['Boletas', 'Facturas', 'Notas de venta', 'CPE admin']}
                series={[
                  {
                    label: 'Monto',
                    data: [
                      datos.boletas.total,
                      datos.facturas.total,
                      datos.notas_venta.total,
                      datos.admin_cpe.total,
                    ],
                  },
                ]}
                format="currency"
              />
            </CardContent>
          </Card>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { etiqueta: 'Boletas', valor: datos.boletas, tono: 'text-brand-600' },
              { etiqueta: 'Facturas', valor: datos.facturas, tono: 'text-brand-600' },
              { etiqueta: 'Notas de venta', valor: datos.notas_venta, tono: 'text-brand-600' },
              { etiqueta: 'CPE de administración', valor: datos.admin_cpe, tono: 'text-emerald-600' },
            ].map((tarjeta) => (
              <Card key={tarjeta.etiqueta}>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    {tarjeta.etiqueta}
                  </p>
                  <p className="text-lg font-bold text-ink-900">{monto(tarjeta.valor.total)}</p>
                  <p className="text-xs text-ink-500">{tarjeta.valor.cantidad} comprobante(s)</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ['POS (boletas, facturas y notas)', datos.total_pos_ventas, datos.total_pos_monto],
              ['Administración (CPE)', datos.total_admin_ventas, datos.total_admin_monto],
              ['Total general', datos.grand_total_ventas, datos.grand_total_monto],
            ].map(([etiqueta, cantidad, importe]) => (
              <Card key={String(etiqueta)}>
                <CardContent className="p-4">
                  <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                    {etiqueta}
                  </p>
                  <p className="text-lg font-bold text-ink-900">{monto(Number(importe))}</p>
                  <p className="text-xs text-ink-500">{Number(cantidad)} comprobante(s)</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {detalle.isLoading ? <Skeleton className="h-60 w-full" /> : null}

          {detalle.data ? (
            <>
              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <ShoppingCart className="size-4 text-brand-500" aria-hidden="true" />
                  POS ({detalle.data.pos.length} de {datos.total_pos_ventas})
                </h3>

                {detalle.data.pos.length === 0 ? (
                  <p className="text-sm text-ink-500">El cliente no tiene comprobantes POS.</p>
                ) : (
                  <div className="overflow-auto rounded-card border border-ink-100">
                    <table className="w-full border-collapse text-[13px]">
                      <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                        <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                          <th className="px-3 py-2">Tipo</th>
                          <th className="px-3 py-2">Comprobante</th>
                          <th className="px-3 py-2">Fecha</th>
                          <th className="px-3 py-2">Cliente</th>
                          <th className="px-3 py-2">Placa</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalle.data.pos.map((fila) => (
                          <tr key={`${fila.tipo_documento}-${fila.id}`} className="border-t border-ink-100">
                            <td className="px-3 py-2 whitespace-nowrap">{fila.tipo_documento}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {fila.serie ?? ''}-{fila.numero ?? ''}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(fila.fecha)}</td>
                            <td className="max-w-[220px] truncate px-3 py-2">{fila.cliente ?? '—'}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{fila.placa ?? '—'}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">{monto(fila.total)}</td>
                            <td className="px-3 py-2">
                              <Badge variant="success">{fila.estado_api ?? '—'}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section>
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink-800">
                  <Wallet className="size-4 text-emerald-600" aria-hidden="true" />
                  Administración ({detalle.data.admin.length} de {datos.total_admin_ventas})
                </h3>

                {detalle.data.admin.length === 0 ? (
                  <p className="text-sm text-ink-500">
                    El cliente no tiene CPE de administración aceptados en el rango.
                  </p>
                ) : (
                  <div className="overflow-auto rounded-card border border-ink-100">
                    <table className="w-full border-collapse text-[13px]">
                      <thead className="bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                        <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                          <th className="px-3 py-2">Comprobante</th>
                          <th className="px-3 py-2">Fecha</th>
                          <th className="px-3 py-2">Cliente</th>
                          <th className="px-3 py-2">Placa</th>
                          <th className="px-3 py-2 text-right">Total</th>
                          <th className="px-3 py-2">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detalle.data.admin.map((fila) => (
                          <tr key={`admin-${fila.id}`} className="border-t border-ink-100">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {fila.serie ?? ''}-{fila.numero ?? ''}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{fechaCorta(fila.fecha)}</td>
                            <td className="max-w-[220px] truncate px-3 py-2">{fila.cliente ?? '—'}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{fila.placa ?? '—'}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">{monto(fila.total)}</td>
                            <td className="px-3 py-2">
                              <Badge variant="success">{fila.estado_api ?? '—'}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            </>
          ) : null}

          <Alert variant="info" title="De dónde salen los datos">
            <span className={ICONO_EN_ALERTA}>
              <Info className="size-4" aria-hidden="true" />
              Las boletas y facturas se cuentan con su `estado_api = 'Aceptado'` y el importe de
              `venta.monto_pagado`; las notas de venta, con `nota_venta.estado = 1`; y los CPE de
              administración con `admin_comprobante.estado_sunat = 'Aceptado'` y el total de la
              venta. El detalle se limita a {detalle.data?.limite ?? 500} filas por tipo para no
              pintar decenas de miles en el navegador (el resumen sí es completo).
            </span>
          </Alert>
        </>
      ) : null}
    </div>
  );
}
